import { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import ActionsIcon from '../../NRIcons/ActionsIcon';
import { CONSTANTS, FEATURE_FLAGS } from '../../constants/constants';
import useNRPaginatedQuery from '../../graphql/useNRPaginatedQuery';
import { DEPENDENCIES } from '../../graphql/queries/dependency';
import { getFilterFieldKey, getFiltersFromContext, mergeDeep, capitalizeFirstLetter } from '../../utils/utils';
import { validateVerificationFilters, isVerificationMethodEnabled } from '../../utils/verificationFilterUtils';
import { validateIdentificationFilters, isIdentificationMethodEnabled } from '../../utils/identificationFilterUtils';
import NRSBOMTable from './NRSBOMTable';
import { SelectionCell } from '../../components/NRReactGridCustomComponents/SelectionCell/SelectionCell';
import isEqual from 'lodash.isequal';
import { SBOM_CONSTANTS } from '../../components/SBOM/SBOM.constants';
import SBOMButton from '../../components/SBOM/SBOMButton/SBOMButton';
import SBOMAddComponentButton from '../../components/SBOM/SBOMButton/SBOMAddComponentButton';
import NRSBOMRemoveModalContent from '../../components/SBOM/RemoveModal/NRSBOMRemoveModalContent';
import NRSBOMAddModalContent from '../../components/SBOM/AddModal/NRSBOMAddModalContent';
import { SBOMContext } from '../../pages/NRSBOMTable/NRSBOMDataShare';
import useLocalStorageService, { LOCAL_STORAGE_CONSTANTS } from '../../services/local';
import { BooleanParam, JsonParam, StringParam, useQueryParam } from 'use-query-params';
import { ArrayObjParam } from '../../services/params.utils';
import useSessionStorageService from '../../services/session';
import NRSBOMActions from '../NRSBOMActions/NRSBOMActions';
import useHasPermission from '../../components/Permissions/useHasPermission';
import Box from '@mui/material/Box';
import WithPermissions from '../../components/Permissions/WithPermissions';
import { useFlag } from '@openfeature/react-sdk';
import { OrgLevelSettingsContext } from '../NRSettings/NRGeneralSettings/NROrgLevelSettingsContextWrapper';
import useFetchOrgLevelSettings from '../NRSettings/NRGeneralSettings/useFetchOrgLevelSettings';

export const SBOM_TABLE_SETTINGS_KEY = 'SbomTableSettings';
const { SORT, FIRST } = LOCAL_STORAGE_CONSTANTS.TABLE;
const { FIELDS } = CONSTANTS.TABLE.SBOM_TABLE.FILTERS;
const { ORDER } = CONSTANTS.TABLE.FILTERS;
const { TRUE, FALSE } = CONSTANTS.TABLE.FILTER_VALUES;
const { EQUAL } = CONSTANTS.FILTER_OPERATIONS;

const { TITLE, ROWS, CORRELATIONS_TOOLTIP_A, CORRELATIONS_TOOLTIP_B } = CONSTANTS.TABLE.SBOM_TABLE;

export function parseData(data, handleActionClick, canWriteAsset) {
  let dataToReturn = [];
  data.forEach((e, i) => {
    if (e?.node) {
      const { dependency, analytic } = e.node;
      let newElement = {
        name: dependency?.name,
        version: dependency?.version?.id,
        identificationIds: dependency?.identificationIds,
        cpes: dependency?.cpes,
        onDisk: dependency?.onDisk,
        isPredicted: dependency?.isPredicted,
        purls: dependency?.purls,
        versionDate: i % 2 === 0,
        vendor: dependency?.vendor,
        type: dependency?.type,
        subtype: dependency?.package?.type,
        license: dependency?.license,
        confidence: dependency?.confidence,
        vulnerability: analytic?.vulnerability,
        analytic: analytic,
        dependency: dependency,
        exploit: analytic?.exploit,
        submitDatetime: e.node.submitDatetime,
        id: e.node.id,
        verification: e.node.verification,
        identifiedVia: e.node.identifiedVia,
        scope: e.node.scope,
        isAi: e.node.isAi,
        aiModelAttributes: e.node.aiModelAttributes, // mocked for now
        aiModelFormat: e.node?.aiModelAttributes?.format || e.node?.aiModelAttributes?.modelAttributes?.format,
        aiProviders: e.node.aiProviders,
        correlations: e.node.correlations,
        latestRemediation: e.node.latestRemediation,
        ...(canWriteAsset
          ? {
              actions: [
                {
                  title: 'Actions',
                  icon: (
                    <IconButton size="small">
                      <ActionsIcon />
                    </IconButton>
                  ),
                  action: handleActionClick,
                  customStyles: {
                    backgroundColor: 'unset'
                  }
                }
              ]
            }
          : {})
      };
      dataToReturn.push(newElement);
    }
  });
  return dataToReturn;
}

export const sbom_columns = [
  {
    title: { value: ROWS.NAME },
    name: 'name'
  },
  {
    title: { value: ROWS.VERSION },
    name: 'version'
  },
  {
    title: { value: ROWS.VENDOR },
    name: 'vendor'
  },
  {
    title: { value: ROWS.TYPE },
    name: 'type',
    type: 'multiselect',
    filterValues: SBOM_CONSTANTS.TYPE_COLUMN_FILTER_VALUES
  },
  {
    title: { value: ROWS.SCOPE },
    name: 'scope',
    hideColumn: true,
    sortDisabled: false,
    type: 'multiselect',
    filterValues: SBOM_CONSTANTS.SCOPE_COLUMN_FILTER_VALUES,
    getCellValue: row => (row.scope ? row.scope.map(s => capitalizeFirstLetter(s.value)).join(', ') : undefined),
    filterMaxWidth: 400 // this has been added here to control filter's maxWidth
  },
  {
    title: { value: ROWS.DEPENDENCIES },
    name: 'dependencies',
    hideFilter: true
  },
  {
    title: { value: ROWS.LICENSE },
    name: 'license',
    sortDisabled: true,
    getCellValue: row => (row.license ? row.license.join(', ') : undefined)
  },
  {
    title: { value: ROWS.CONFIDENCE },
    name: 'confidence',
    type: 'multiselectEnum',
    filterValues: CONSTANTS.TABLE.SBOM_TABLE.CONFIDENCE_FILTER_VALUES
  },
  {
    title: { value: ROWS.VERIFIED_VIA },
    name: 'verification',
    hideColumn: true,
    sortDisabled: true,
    type: 'multiselect',
    filterValues: CONSTANTS.TABLE.SBOM_TABLE.VERIFICATION_FILTER_VALUES
  },
  {
    title: { value: ROWS.IDENTIFIED_VIA },
    name: 'identifiedVia',
    hideColumn: true,
    hideFilter: true,
    sortDisabled: true,
    type: 'multiselect',
    filterValues: CONSTANTS.TABLE.SBOM_TABLE.IDENTIFICATION_FILTER_VALUES,
    filterMaxWidth: 180 // this has been added here to control filter's maxWidth
  },
  {
    title: { value: ROWS.ONDISK },
    name: 'onDisk',
    type: 'booleanBE',
    filterValues: { all: null, true: TRUE, false: FALSE },
    hideColumn: true
  },
  {
    title: { value: ROWS.PREDICTED },
    name: 'isPredicted',
    type: 'booleanBE',
    filterValues: { all: null, true: TRUE, false: FALSE },
    hideColumn: true
  },
  {
    name: 'correlations',
    title: { value: ROWS.CORRELATIONS },
    type: 'number',
    tooltipA: CORRELATIONS_TOOLTIP_A,
    tooltipB: CORRELATIONS_TOOLTIP_B
  },
  {
    title: { value: ROWS.CVES },
    name: 'vulnerability',
    hideFilter: true
  },
  {
    title: { value: ROWS.EXPLOITS },
    name: 'exploit',
    hideFilter: true
  },
  {
    title: { value: '' },
    name: 'latestRemediation',
    hideFilter: true,
    sortDisabled: true,
    width: 48,
    allowReordering: false
  }
];

export function getVulnerabilityFilter(selectedSeverityFilters) {
  return { vulnerability: { any: selectedSeverityFilters.map(severity => severity.toLowerCase()) } };
}

const take = 25;

function NRSBOMTableContainer({ sbom_column_extensions }) {
  const { componentId } = useParams();
  const tableRef = useRef();
  const {
    openRemoveModal,
    selectedComponents,
    openAddDependencyModal,
    openEditDependencyModal,
    editingComponent,
    setSelectedComponents,
    setOpenRemoveModal,
    setOpenAddDependencyModal,
    setOpenEditDependencyModal,
    setEditingComponent,
    enabledVerificationMethods,
    enabledIdentificationMethods,
    setEnabledIdentificationMethods
  } = useContext(SBOMContext);
  // const { showUnremediated } = useContext(RemediationContext);
  const { setSessionDataWithSetter } = useSessionStorageService();

  const { orgLevelSettings, setRefetch } = useContext(OrgLevelSettingsContext);
  useFetchOrgLevelSettings();

  const [columnFilter, setColumnFilter] = useQueryParam('componentColumnFilter', ArrayObjParam);
  const [componentSeverityFilter] = useQueryParam('componentSeverityFilter', JsonParam);
  const [componentHasExploits] = useQueryParam('componentHasExploits', BooleanParam);
  const [componentIsAi] = useQueryParam('componentIsAi', BooleanParam);
  const [componentAiProvider] = useQueryParam('componentAiProvider', StringParam);
  const { value: isComponentIdentifiedViaEnabled } = useFlag(FEATURE_FLAGS.COMPONENT_IDENTIFIED_VIA, false);
  const { value: isComponentConfidenceEnabled } = useFlag(FEATURE_FLAGS.COMPONENT_CONFIDENCE, false);
  const { value: isAiRelatedDependenciesEnabled } = useFlag(FEATURE_FLAGS.AI_RELATED_DEPENDENCIES, false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [hasExploitsQueryFilter, setHasExploitsQueryFilter] = useState(!!componentHasExploits ? { exploit: { has: { eq: true } } } : {});
  const [aiRelatedFieldFilters, setAiRelatedFieldFilters] = useState(
    isAiRelatedDependenciesEnabled && !!componentIsAi
      ? [
          { fieldName: FIELDS.isAi, value: true, operation: EQUAL },
          ...(componentAiProvider ? [{ fieldName: FIELDS.aiProviders, value: componentAiProvider, operation: EQUAL }] : [])
        ]
      : []
  );

  const [querySeverityFilter, setQuerySeverityFilter] = useState(
    !!componentSeverityFilter?.length ? getVulnerabilityFilter(componentSeverityFilter) : {}
  );
  const canWriteAsset = useHasPermission({ permission: CONSTANTS.PERMISSIONS.WRITE_IMGS });

  useEffect(() => {
    if (isComponentIdentifiedViaEnabled) {
      setRefetch(true);
    }
  }, [isComponentIdentifiedViaEnabled, setRefetch]);

  useEffect(() => {
    let newSeverityFilter = {};
    if (componentSeverityFilter?.length) {
      newSeverityFilter = getVulnerabilityFilter(componentSeverityFilter);
    }
    setQuerySeverityFilter(newSeverityFilter);
  }, [componentSeverityFilter]);

  useEffect(() => {
    if (componentHasExploits) {
      const exploit = {
        has: {
          eq: componentHasExploits
        }
      };
      setHasExploitsQueryFilter({ exploit });
    } else {
      setHasExploitsQueryFilter({});
    }
  }, [componentHasExploits]);

  useEffect(() => {
    if (isAiRelatedDependenciesEnabled && componentIsAi) {
      const fieldFilters = [{ fieldName: FIELDS.isAi, value: true, operation: EQUAL }];
      if (componentAiProvider) {
        fieldFilters.push({ fieldName: FIELDS.aiProviders, value: componentAiProvider, operation: EQUAL });
      }
      setAiRelatedFieldFilters(fieldFilters);
    } else {
      setAiRelatedFieldFilters([]);
    }
  }, [componentIsAi, componentAiProvider, isAiRelatedDependenciesEnabled]);

  const { getStorageKeyData } = useLocalStorageService();
  const sbomSettingsStoredData = getStorageKeyData(SBOM_TABLE_SETTINGS_KEY);
  const localPageSize = sbomSettingsStoredData?.[FIRST] || take;

  const [sort, setSort] = useState(
    sbomSettingsStoredData?.[SORT] || {
      field: FIELDS.name,
      order: ORDER.ASC
    }
  );

  const mergedFieldFilters = useMemo(
    () => [...(columnFilter?.length ? columnFilter : []), ...aiRelatedFieldFilters],
    [columnFilter, aiRelatedFieldFilters]
  );

  const [ownQueryFilter, setOwnQueryFilter] = useState(
    mergeDeep(
      {},
      { analyticFilter: { ...querySeverityFilter, ...hasExploitsQueryFilter } },
      mergedFieldFilters.length ? { fields: mergedFieldFilters } : {}
    )
  );

  const saveFilters = filters => {
    const filtersToSend = filters?.map(e => {
      return {
        fieldName: e?.fieldName || e?.columnName?.toUpperCase(),
        value: e?.value,
        operation: e?.operation?.toUpperCase()
      };
    });
    setSessionDataWithSetter(setColumnFilter, filtersToSend, 'componentColumnFilter');
  };

  const isUpdatingFilters = useRef(false);

  const handleFiltersChange = filters => {
    const afterVerification = validateVerificationFilters(filters, enabledVerificationMethods);
    const validatedFilters = validateIdentificationFilters(afterVerification, enabledIdentificationMethods);

    isUpdatingFilters.current = true;
    saveFilters(validatedFilters);

    setTimeout(() => {
      isUpdatingFilters.current = false;
    }, 100);
  };

  useEffect(() => {
    if (columnFilter?.length && !isUpdatingFilters.current) {
      const afterVerification = validateVerificationFilters(columnFilter, enabledVerificationMethods);
      const validatedFilters = validateIdentificationFilters(afterVerification, enabledIdentificationMethods);

      if (!isEqual(columnFilter, validatedFilters)) {
        isUpdatingFilters.current = true;
        saveFilters(validatedFilters);
        setTimeout(() => {
          isUpdatingFilters.current = false;
        }, 100);
      }
    }
  }, [columnFilter, enabledVerificationMethods, enabledIdentificationMethods]);

  useEffect(() => {
    // handles the local filters
    const newFilters = mergeDeep(
      {},
      { analyticFilter: { ...querySeverityFilter, ...hasExploitsQueryFilter } },
      mergedFieldFilters.length ? { fields: mergedFieldFilters } : {}
    );
    if (!isEqual(ownQueryFilter, newFilters)) {
      setOwnQueryFilter(newFilters);
    }
  }, [querySeverityFilter, hasExploitsQueryFilter, mergedFieldFilters, ownQueryFilter]);

  const [tableData, isSBOMDataLoading, fetchMore, pageInfo, called, totalCount, variables, refetch] = useNRPaginatedQuery({
    query: DEPENDENCIES,
    options: {
      context: { version: 'v3' },
      fetchPolicy: 'no-cache',
      variables: {
        args: {
          filter: ownQueryFilter,
          composedAssetId: componentId,
          // showUnremediated,
          cursor: { first: tableRef?.current?.tablePageSize || localPageSize },
          sort: {
            field: sort.field,
            order: sort.order
          }
        },
        includeIdentifiedVia: isComponentIdentifiedViaEnabled,
        includeConfidence: isComponentConfidenceEnabled,
        includeAiRelatedDependencies: isAiRelatedDependenciesEnabled
      }
    },
    qlObjKey: 'dependencies',
    defaultValue: [],
    transformData: data => parseData(data, handleActionClick, canWriteAsset)
  });

  const handleOnSortingChange = (field, order) => {
    let sortValue = {
      field,
      order
    };
    setSort(sortValue);
    fetchMore({
      args: {
        filter: ownQueryFilter,
        composedAssetId: componentId,
        // showUnremediated,
        cursor: { first: tableRef?.current?.tablePageSize },
        sort: sortValue
      },
      includeIdentifiedVia: isComponentIdentifiedViaEnabled,
      includeConfidence: isComponentConfidenceEnabled,
      includeAiRelatedDependencies: isAiRelatedDependenciesEnabled
    });
    tableRef?.current?.refreshPagination();
  };

  const handleFetchMore = cursor => {
    fetchMore({
      args: {
        filter: ownQueryFilter,
        composedAssetId: componentId,
        // showUnremediated,
        cursor,
        sort
      },
      includeIdentifiedVia: isComponentIdentifiedViaEnabled,
      includeConfidence: isComponentConfidenceEnabled,
      includeAiRelatedDependencies: isAiRelatedDependenciesEnabled
    });
  };

  const handleModalClose = () => {
    setOpenAddDependencyModal(false);
    setOpenEditDependencyModal(false);
    setOpenRemoveModal(false);
    setSelectedComponents(null);
    setEditingComponent(null);
    setAnchorEl(null);
  };

  const handleActionClick = (event, gridActions, row) => {
    const identificationIds = row?.dependency?.identificationIds;
    setAnchorEl(event.currentTarget);
    !!row && setSelectedComponents({ assetId: componentId, identificationIds });
    !!row && setEditingComponent(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRefreshTable = useCallback(() => {
    refetch();
  }, [refetch]);

  function handleEditClick() {
    setOpenEditDependencyModal(true);
    handleMenuClose();
  }

  useEffect(() => {
    tableRef?.current?.refreshPagination();
  }, [ownQueryFilter]);

  useEffect(() => {
    return () => {
      tableRef?.current?.clearTableFilters();
    };
  }, []);

  const fieldFiltersFromContext = useMemo(() => getFiltersFromContext(columnFilter, FIELDS), [columnFilter]);

  useEffect(() => {
    if (!orgLevelSettings) return;
    setEnabledIdentificationMethods({
      symbolIndex: orgLevelSettings.symbolIndexEnabled?.enabled ?? true,
      kernelModule: orgLevelSettings.kernelModuleEnabled?.enabled ?? true,
      curatedHash: orgLevelSettings.curatedHashEnabled?.enabled ?? true,
      legacyHash: orgLevelSettings.legacyHashEnabled?.enabled ?? true,
      packageManifest: orgLevelSettings.packageManifestEnabled?.enabled ?? true,
      signature: orgLevelSettings.signatureEnabled?.enabled ?? true,
      peMetaData: orgLevelSettings.peMetaDataEnabled?.enabled ?? true,
      libraryVersion: orgLevelSettings.libraryVersionEnabled?.enabled ?? true,
      libraryName: orgLevelSettings.libraryNameEnabled?.enabled ?? true,
      binaryFingerprint: orgLevelSettings.binaryFingerprintEnabled?.enabled ?? true
    });
  }, [orgLevelSettings, setEnabledIdentificationMethods]);

  const filteredVerificationValues = useMemo(() => {
    if (!enabledVerificationMethods) return CONSTANTS.TABLE.SBOM_TABLE.VERIFICATION_FILTER_VALUES;

    return CONSTANTS.TABLE.SBOM_TABLE.VERIFICATION_FILTER_VALUES.filter(filterValue =>
      isVerificationMethodEnabled(filterValue.value, enabledVerificationMethods)
    );
  }, [enabledVerificationMethods]);

  const filteredIdentificationValues = useMemo(() => {
    if (!enabledIdentificationMethods) return CONSTANTS.TABLE.SBOM_TABLE.IDENTIFICATION_FILTER_VALUES;

    return CONSTANTS.TABLE.SBOM_TABLE.IDENTIFICATION_FILTER_VALUES.filter(filterValue =>
      isIdentificationMethodEnabled(filterValue.value, enabledIdentificationMethods)
    );
  }, [enabledIdentificationMethods]);

  const columnsWithFilteredVerification = useMemo(() => {
    return sbom_columns
      .filter(column => {
        // Exclude verification column when identifiedVia is enabled
        if (column.name === 'verification' && isComponentIdentifiedViaEnabled) {
          return false;
        }
        // Exclude identifiedVia column when flag is disabled
        if (column.name === 'identifiedVia' && !isComponentIdentifiedViaEnabled) {
          return false;
        }
        // Exclude confidence column when flag is disabled
        if (column.name === 'confidence' && !isComponentConfidenceEnabled) {
          return false;
        }
        return true;
      })
      .map(column => {
        // Update filter values for verification column when it's included
        if (column.name === 'verification') {
          return {
            ...column,
            filterValues: filteredVerificationValues
          };
        }
        // Update filter values for identifiedVia column when it's included
        if (column.name === 'identifiedVia') {
          return {
            ...column,
            filterValues: filteredIdentificationValues
          };
        }
        return column;
      });
  }, [filteredVerificationValues, filteredIdentificationValues, isComponentIdentifiedViaEnabled, isComponentConfidenceEnabled]);

  const SBOMSelectionCell = props => {
    const { row } = props;
    const componentName = row?.name || 'component';
    const version = row?.version ? ` version ${row.version}` : '';
    const ariaLabel = `Select component ${componentName}${version}`;

    return <SelectionCell {...props} borderColor={null} ariaLabel={ariaLabel} />;
  };

  return (
    <>
      <NRSBOMTable
        ref={tableRef}
        loading={isSBOMDataLoading}
        rows={tableData}
        columns={columnsWithFilteredVerification}
        columnExtensions={sbom_column_extensions}
        title={TITLE}
        showSelect={true}
        pageSize={localPageSize}
        hasCursorPagination={true}
        preSettedFilters={fieldFiltersFromContext}
        showIntegratedPagination={true}
        selectionCell={SBOMSelectionCell}
        selectAllAriaLabel="Select all components"
        titleExtraComponent={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}
          >
            <WithPermissions permission={CONSTANTS.PERMISSIONS.WRITE_IMGS}>
              <SBOMAddComponentButton key="SBOMAddComponentButton" />
            </WithPermissions>
            <SBOMButton key="sbom" />
          </Box>
        }
        sx={{
          '& > div': {
            display: 'flex',
            flexDirection: 'column'
          }
        }}
        defaultSortingColumn={getFilterFieldKey(sort.field, FIELDS)}
        defaultSortingOrder={sort.order}
        totalCount={totalCount}
        handleFetchMore={handleFetchMore}
        pageInfo={pageInfo}
        onSortingChange={handleOnSortingChange}
        handleFiltersChange={handleFiltersChange}
        sbomTableKey={SBOM_TABLE_SETTINGS_KEY}
        sortingColumnNames={FIELDS}
        selectAllFetchConfig={{ qlArgs: { assetId: componentId, filter: ownQueryFilter } }}
        hasExtraActions={canWriteAsset}
        showIdentifiedVia={isComponentIdentifiedViaEnabled} // TODO: remove this once the feature flag is removed
        showConfidence={isComponentConfidenceEnabled}
      />
      <NRSBOMActions
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        onMenuClose={() => handleMenuClose()}
        handleEditClick={() => handleEditClick()}
        disabled={editingComponent?.dependency?.type === SBOM_CONSTANTS.COMPONENT_TYPE.ROOT}
      />
      {openRemoveModal && <NRSBOMRemoveModalContent onModalClose={handleModalClose} component={selectedComponents} onRefresh={handleRefreshTable} />}
      {openAddDependencyModal && <NRSBOMAddModalContent composedAssetId={componentId} onClose={handleModalClose} onRefresh={handleRefreshTable} />}
      {openEditDependencyModal && editingComponent && (
        <NRSBOMAddModalContent
          composedAssetId={componentId}
          onClose={handleModalClose}
          onRefresh={handleRefreshTable}
          isEditMode={true}
          initialValues={editingComponent}
        />
      )}
    </>
  );
}
export default NRSBOMTableContainer;
